// Team Notes Section Component - Editable notes with manual save buttons

import React, { useState } from 'react';
import type { 
  TeamNote, 
  TeamNoteAction,
  UseTeamTranscriptDetailReturn 
} from '../../types/teamTranscripts';

interface TeamNotesSectionProps {
  teamId: string;
  transcriptId: string;
  notes: TeamNote[];
  canEdit: boolean;
  saving: boolean;
  onUpdateNotes: UseTeamTranscriptDetailReturn['updateNotes'];
  onNotesUpdated: () => void;
}

const TeamNotesSection: React.FC<TeamNotesSectionProps> = ({
  teamId,
  transcriptId,
  notes,
  canEdit,
  saving,
  onUpdateNotes,
  onNotesUpdated
}) => {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [localSaving, setLocalSaving] = useState(false);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return 'Unknown date';
    }
  };

  const handleAddNote = async () => {
    if (!noteTitle.trim() || !noteContent.trim()) return;

    setLocalSaving(true);
    try {
      const result = await onUpdateNotes('add', { 
        title: noteTitle.trim(),
        content: noteContent.trim() 
      });
      if (result.success) {
        setNoteTitle('');
        setNoteContent('');
        setIsAddingNote(false);
        onNotesUpdated();
      } else {
        alert(result.error || 'Failed to add note');
      }
    } catch (error) {
      console.error('Failed to add note:', error);
      alert('Failed to add note');
    } finally {
      setLocalSaving(false);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!noteTitle.trim() || !noteContent.trim()) return;

    setLocalSaving(true);
    try {
      const result = await onUpdateNotes('update', { 
        noteId, 
        title: noteTitle.trim(),
        content: noteContent.trim() 
      });
      if (result.success) {
        setEditingNoteId(null);
        setNoteTitle('');
        setNoteContent('');
        onNotesUpdated();
      } else {
        alert(result.error || 'Failed to update note');
      }
    } catch (error) {
      console.error('Failed to update note:', error);
      alert('Failed to update note');
    } finally {
      setLocalSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    setLocalSaving(true);
    try {
      const result = await onUpdateNotes('delete', { noteId });
      if (result.success) {
        onNotesUpdated();
      } else {
        alert(result.error || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
      alert('Failed to delete note');
    } finally {
      setLocalSaving(false);
    }
  };

  const startEditing = (note: TeamNote) => {
    setEditingNoteId(note.id);
    setNoteTitle(note.title || '');
    setNoteContent(note.content);
    setIsAddingNote(false);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setIsAddingNote(false);
    setNoteTitle('');
    setNoteContent('');
  };

  const startAdding = () => {
    setIsAddingNote(true);
    setEditingNoteId(null);
    setNoteTitle('');
    setNoteContent('');
  };

  const isSaving = saving || localSaving;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          📝 Team Notes
          <span className="text-sm font-normal text-gray-500">
            ({notes.length}/3)
          </span>
        </h3>
        {canEdit && notes.length < 3 && !isAddingNote && !editingNoteId && (
          <button
            onClick={startAdding}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Note
          </button>
        )}
      </div>

      {/* Add Note Form */}
      {isAddingNote && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Team Note</h4>
          <div className="space-y-3">
            <div>
              <input
                type="text"
                placeholder="Note title..."
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={100}
              />
            </div>
            <div>
              <textarea
                placeholder="Note content..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                maxLength={1000}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {noteContent.length}/1000 characters
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={cancelEditing}
                  disabled={isSaving}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  disabled={isSaving || !noteTitle.trim() || !noteContent.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving && (
                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                  )}
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes List */}
      {notes.length === 0 && !isAddingNote ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-3">📝</div>
          <p className="text-sm">
            {canEdit 
              ? 'No team notes yet. Add your first note to get started.' 
              : 'No team notes available for this transcript.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              {editingNoteId === note.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <div>
                    <input
                      type="text"
                      value={noteTitle}
                      onChange={(e) => setNoteTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <textarea
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      maxLength={1000}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {noteContent.length}/1000 characters
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={cancelEditing}
                        disabled={isSaving}
                        className="px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleUpdateNote(note.id)}
                        disabled={isSaving || !noteTitle.trim() || !noteContent.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isSaving && (
                          <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                        )}
                        Update
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-2">{note.title}</h4>
                      <p className="text-gray-800 text-sm whitespace-pre-wrap leading-relaxed">
                        {note.content}
                      </p>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1 ml-4">
                        <button
                          onClick={() => startEditing(note)}
                          disabled={isSaving}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                          title="Edit note"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={isSaving}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                          title="Delete note"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Created: {formatDate(note.created_at)}</span>
                    {note.updated_at !== note.created_at && (
                      <span>Updated: {formatDate(note.updated_at)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Maximum notes reached message */}
      {notes.length >= 3 && !editingNoteId && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Maximum of 3 notes per transcript. Delete a note to add a new one.</span>
          </div>
        </div>
      )}

      {!canEdit && notes.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>You have view-only access to these team notes.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamNotesSection;